import MarkdownIt from 'markdown-it';
import { InputRule, inputRules } from 'prosemirror-inputrules';
import {
  MessageMarkdownSerializer,
  MessageMarkdownTransformer,
} from '@chatwoot/prosemirror-schema';

// A classe vem da PRÓPRIA instância do pacote, e não de um
// `import { MarkdownSerializer } from 'prosemirror-markdown'`.
//
// Dois motivos, o primeiro medido: `prosemirror-markdown` é dependência
// TRANSITIVA do `@chatwoot/prosemirror-schema`, e o pnpm deste projeto é
// estrito — ela não existe na raiz do `node_modules`, então aquele import
// simplesmente não resolve. Conferido com `require.resolve`.
//
// O segundo é melhor ainda: pegando do objeto que já existe, usamos
// exatamente a mesma classe que o pacote usa. Não há como aparecer uma
// segunda cópia da biblioteca com outra versão.
const MarkdownSerializer = MessageMarkdownSerializer.constructor;

/**
 * O markdown do advup que NÃO come as linhas em branco.
 *
 * ── O DEFEITO (relatado pelo Victor em 11/09/2026) ──────────────────────────
 * A Dra. Jullya formata uma resposta pronta com negrito e linhas em branco
 * entre os blocos, salva, reabre — e o texto voltou diferente: espaços a
 * menos e barras invertidas no meio.
 *
 * MEDIDO, com o próprio parser e serializador do editor:
 *
 *   entra:  "Um.\n\n\nDois."
 *   parser devolve: DOIS parágrafos — o vazio já não existe
 *   sai:    "Um.\n\nDois."          ← a linha em branco morreu na LEITURA
 *
 *   e um parágrafo vazio de verdade no documento:
 *   sai:    "Um.\n\n\\\nDois."      ← escrito como BARRA INVERTIDA
 *
 * São duas perdas somadas, uma em cada ponta:
 *
 *  1. NA ESCRITA, `@chatwoot/prosemirror-schema` serializa parágrafo vazio
 *     como `\\\n` (a linha
 *     `state.write(index > 0 && hasContentAfter(...) ? '\\\n' : '\n')`).
 *     Barra invertida é quebra de linha válida em CommonMark, mas não é linha
 *     em branco: ela vira UMA quebra dentro do mesmo parágrafo, e não o
 *     respiro que a pessoa pediu. E aparece crua em qualquer lugar que mostre
 *     o texto sem renderizar — foi assim que ela apareceu na lista de
 *     respostas prontas.
 *
 *  2. NA LEITURA, CommonMark trata "duas ou mais linhas em branco" como
 *     equivalente a uma. É a regra do formato, não um defeito do parser — mas
 *     significa que qualquer respiro extra se perde ao reabrir.
 *
 * ── A CORREÇÃO ─────────────────────────────────────────────────────────────
 * Os dois lados, porque consertar um só não fecha o ciclo:
 *
 *  ESCRITA → parágrafo vazio vira uma linha em branco de verdade (`\n`), e
 *  não `\\\n`. O texto gravado fica limpo, e o servidor já sabe preservá-lo:
 *  `Messages::MarkdownRendererService#preserve_multiple_newlines` troca as
 *  sequências de `\n` por um marcador antes de renderizar, exatamente por
 *  causa desta regra do CommonMark. Ou seja: o que o cliente recebe já estava
 *  certo — quem perdia era a tela.
 *
 *  LEITURA → antes de entregar o texto ao parser, cada linha em branco EXTRA
 *  vira uma linha com um marcador que o CommonMark preserva como parágrafo.
 *  Depois de montado o documento, esses parágrafos-marcador voltam a ser
 *  parágrafos vazios. O marcador nunca chega ao texto gravado.
 *
 * É a MESMA técnica do lado Ruby, do outro lado do fio.
 */

// Espaço de largura zero. Escolhido por ser um caractere que o CommonMark
// aceita como conteúdo de parágrafo (então a linha sobrevive à leitura) e que
// não existe em texto escrito à mão — não há risco de confundir com algo que
// a pessoa digitou.
const MARCA = '​';

/**
 * Cada linha em branco além da primeira vira uma linha com a marca.
 *
 * ATENÇÃO À EXPRESSÃO: linha em branco NÃO é só `\n\n`. Uma linha com espaços
 * dentro (`\n \n`) também é linha em branco para o CommonMark — e é o caso
 * COMUM, não a exceção.
 *
 * Foi o defeito que o Victor cansou de me apontar em 11/09/2026. Gravado no
 * banco estava `Abraços! \n \n\n**Ola**`: a linha do meio tinha UM ESPAÇO.
 * Minha primeira versão procurava `\n{3,}`, quebras puras, e passava reto por
 * ela. Os textos dele são cheios de espaço no fim das linhas, então quase toda
 * linha em branco dele caía nesse buraco.
 *
 * Quantas linhas em branco existem = quantas quebras tem o trecho, menos 1.
 * Contar QUEBRAS, e não o comprimento do trecho, é o que torna o espaço
 * irrelevante.
 */
export const marcarLinhasEmBranco = texto =>
  String(texto ?? '').replace(/\n(?:[ \t]*\n)+/g, trecho => {
    const emBranco = (trecho.match(/\n/g) || []).length - 1;
    const extras = emBranco - 1; // "\n\n" é o separador normal de parágrafo
    if (extras <= 0) return '\n\n';
    return `\n\n${`${MARCA}\n\n`.repeat(extras)}`;
  });

/** Os parágrafos-marcador voltam a ser parágrafos vazios de verdade. */
const limparMarcas = (doc, schema) => {
  const filhos = [];
  doc.forEach(no => {
    const soMarca =
      no.type === schema.nodes.paragraph && no.textContent === MARCA;
    filhos.push(soMarca ? schema.nodes.paragraph.create() : no);
  });
  return schema.node('doc', doc.attrs, filhos);
};

/**
 * ── O ASTERISCO ─────────────────────────────────────────────────────────────
 * No WhatsApp, *texto* é NEGRITO. No markdown do mundo inteiro, *texto* é
 * itálico e o negrito são dois asteriscos. A Dra. Jullya digita como o
 * WhatsApp ensinou, e saía itálico.
 *
 * A regra escolhida (Victor, 11/09/2026): ELA DIGITA COMO NO WHATSAPP, O
 * SISTEMA GUARDA NO PADRÃO.
 *
 * Por que guardar no padrão e não como ela digitou, que seria mais literal:
 * porque o texto gravado é lido DE NOVO pelo servidor na hora de enviar
 * (`Messages::MarkdownRendererService`, que usa CommonMarker, markdown
 * padrão). Se eu gravasse `*ola*` querendo negrito, o servidor leria itálico
 * e mandaria `_ola_` para o celular do cliente — itálico. Medido em
 * `whats_app_renderer.rb:7` (strong → `*`) e `:11` (emph → `_`).
 *
 * Então:
 *   ela digita `*ola*`   → negrito na tela → guarda `**ola**` → chega `*ola*`
 *                                                               = NEGRITO ✓
 *   ela digita `**ola**` → negrito na tela → guarda `**ola**` → NEGRITO ✓
 *   ela digita `_ola_`   → itálico na tela → guarda `_ola_`   → ITÁLICO ✓
 *
 * O itálico PRECISA ser gravado com sublinhado. Se continuasse saindo com um
 * asterisco, no salvamento seguinte a regra de cima o leria como negrito e o
 * itálico dela viraria negrito sozinho. Foi o que me fez trocar os dois.
 */
const negritoComUmAsterisco = estado => {
  estado.tokens.forEach(bloco => {
    if (bloco.type !== 'inline' || !bloco.children) return;
    bloco.children.forEach(pedaco => {
      // Só o asterisco. Sublinhado (`_ola_`) segue sendo itálico, e o negrito
      // de dois asteriscos já chega aqui como `strong`.
      if (pedaco.markup !== '*') return;
      if (pedaco.type === 'em_open') {
        pedaco.type = 'strong_open';
        pedaco.tag = 'strong';
      } else if (pedaco.type === 'em_close') {
        pedaco.type = 'strong_close';
        pedaco.tag = 'strong';
      }
    });
  });
};

// Cópia fiel da configuração do pacote (messageParser.js:36-48), porque o
// `md` de lá é um só, compartilhado por toda a aplicação: mexer nele seria um
// efeito colateral invisível em todo editor de mensagem. Aqui cada editor
// ganha o seu.
const criarTokenizer = () => {
  const md = MarkdownIt('commonmark', { html: false, linkify: false });
  md.enable(['entity', 'escape']);
  md.disable(['table', 'hr', 'heading', 'lheading'], true);
  md.core.ruler.push('advup-negrito-com-um-asterisco', negritoComUmAsterisco);
  return md;
};

export class TransformerComEspacos extends MessageMarkdownTransformer {
  constructor(schema) {
    super(schema, criarTokenizer());
  }

  parse(conteudo) {
    const doc = super.parse(marcarLinhasEmBranco(conteudo));
    return limparMarcas(doc, doc.type.schema);
  }
}

export const criarTransformerComEspacos = schema =>
  new TransformerComEspacos(schema);

/**
 * O serializador do pacote, com UMA regra trocada: parágrafo vazio.
 *
 * `MarkdownSerializer` guarda os handlers em `nodes` e `marks`, então dá para
 * reaproveitar todos os outros sem copiar nenhum — se o Chatwoot mudar a
 * serialização de lista, link ou menção, herdamos a mudança.
 */
const paragrafoComEspaco = (state, node, parent) => {
  const vazio = node.childCount === 0 || !node.textContent.trim();
  if (vazio) {
    // Sozinho no documento não vira nada: um parágrafo vazio solto não é
    // respiro, é documento vazio.
    if (parent.childCount === 1) return;
    // Uma linha em branco, nunca `\`.
    //
    // E SEM `closeBlock` — foi o erro da minha primeira versão, medido no
    // teste: o parágrafo anterior já fechou com `\n\n`, então escrever aqui
    // e fechar de novo somava DUAS linhas em branco onde a pessoa pediu uma.
    // `Um.\n\n\nDois.` virava `Um.\n\n\n\nDois.` e crescia a cada
    // salvamento. O original do pacote também não fecha; só o conteúdo que
    // escrevi é diferente.
    state.write('\n');
    return;
  }
  state.renderInline(node);
  state.closeBlock(node);
};

// Quebra simples (Shift+Enter): escreve a quebra e mais nada.
//
// O original escreve `\\` antes da quebra, que é a forma canônica do
// CommonMark de forçar uma quebra dentro do parágrafo. Aqui não precisa:
// o parser deste mesmo pacote mapeia `softbreak: { node: 'hard_break' }`
// (parser.js:33), ou seja, uma quebra nua já volta como quebra de verdade,
// não vira espaço. A barra era o único caractere que o sistema acrescentava
// sozinho ao texto salvo.
const quebraSemBarra = state => state.write('\n');

/**
 * ── SHIFT+ENTER ─────────────────────────────────────────────────────────────
 * Foi o que o Victor estava apertando (11/09/2026). No keymap do pacote,
 * Shift+Enter (e Cmd+Enter) NÃO cria parágrafo: insere UM ESPAÇO e uma
 * quebra de linha DENTRO do parágrafo (`insertText(' ')` +
 * `replaceSelectionWith(hard_break)`, keymap.js:83-90). Duas vezes seguidas,
 * ou uma vez no fim do parágrafo, aparece na tela como linha em branco — mas
 * ao gravar vira `\n\n`, e `\n\n` para o markdown é só "parágrafo novo", sem
 * linha em branco. Medido no banco: `Abraços! [n][n]teste` — o espaço é o do
 * Shift+Enter, e a linha em branco que ele viu na tela sumiu.
 *
 * A saída: antes de gravar, as quebras dentro do parágrafo que aparecem como
 * linha em branco viram parágrafos vazios de verdade — a mesma coisa que dois
 * Enters produziriam — e daí o resto deste arquivo já sabe cuidar.
 *
 *   uma quebra no meio  → continua quebra (`a\nb`)
 *   k quebras no meio   → o texto de cada lado vira parágrafo, com k-1 vazios
 *                         entre eles (é o que se vê na tela: k-1 linhas em
 *                         branco)
 *   k quebras no fim    → k parágrafos vazios depois
 *   k quebras no início → k parágrafos vazios antes
 *
 * Trechos só de espaço entre quebras (o espaço que o Shift+Enter insere)
 * contam como nada, e as quebras dos dois lados se somam.
 */
const soEspaco = nos => nos.every(n => n.isText && !n.text.trim());

// Tira o espaço do fim do trecho — é o espaço que o Shift+Enter insere antes
// da quebra. No fim de um parágrafo ele é invisível e o markdown o descarta
// de qualquer jeito; melhor não gravar.
const semEspacoNoFim = nos => {
  const ultimo = nos[nos.length - 1];
  if (!ultimo?.isText) return nos;
  const texto = ultimo.text.replace(/\s+$/, '');
  const resto = nos.slice(0, -1);
  return texto ? [...resto, ultimo.withText(texto)] : resto;
};

const quebrasComoParagrafos = (paragrafo, schema) => {
  const { hard_break: quebra, paragraph: tipoParagrafo } = schema.nodes;
  let temQuebra = false;
  paragrafo.forEach(no => {
    if (no.type === quebra) temQuebra = true;
  });
  // Sem quebra dentro, não há o que normalizar — inclusive o parágrafo vazio,
  // que precisa continuar existindo.
  if (!temQuebra) return [paragrafo];

  // Trechos de conteúdo e, entre eles, quantas quebras há.
  // `entre[i]` = quebras entre trechos[i] e trechos[i+1].
  const trechos = [];
  const entre = [];
  let trecho = [];
  let contagem = 0;
  paragrafo.forEach(no => {
    if (no.type === quebra) {
      contagem += 1;
      return;
    }
    if (contagem > 0) {
      trechos.push(trecho);
      entre.push(contagem);
      trecho = [];
      contagem = 0;
    }
    trecho.push(no);
  });
  trechos.push(trecho);
  if (contagem > 0) {
    entre.push(contagem);
    trechos.push([]);
  }

  // Trecho só de espaço (o espaço do Shift+Enter) não é conteúdo: some, e as
  // quebras dos dois lados se somam.
  for (let i = trechos.length - 2; i >= 1; i -= 1) {
    if (soEspaco(trechos[i])) {
      entre[i - 1] += entre[i];
      trechos.splice(i, 1);
      entre.splice(i, 1);
    }
  }

  // Só quebras simples entre trechos com conteúdo: é o caso comum, e o
  // parágrafo fica exatamente como está (inclusive as marcas da quebra).
  if (
    entre.every(q => q === 1) &&
    trechos[0].length &&
    trechos[trechos.length - 1].length
  ) {
    return [paragrafo];
  }

  const saida = [];
  const novoParagrafo = filhos => tipoParagrafo.create(paragrafo.attrs, filhos);
  let atual = [];
  trechos.forEach((t, i) => {
    atual.push(...t);
    if (i === trechos.length - 1) return;
    const q = entre[i];
    const proximoTemConteudo = trechos[i + 1].length > 0;
    if (q === 1 && atual.length && proximoTemConteudo) {
      // Quebra simples, continua no parágrafo — herdando as marcas do texto
      // anterior, para um negrito que atravessa a quebra continuar inteiro.
      atual.push(quebra.create(null, null, atual[atual.length - 1].marks));
      return;
    }
    // Linha em branco na tela: fecha o parágrafo e abre vazios.
    // Entre dois trechos com conteúdo, k quebras mostram k-1 linhas em
    // branco; no início ou no fim, k quebras mostram k.
    if (atual.length) saida.push(novoParagrafo(semEspacoNoFim(atual)));
    const vazios = atual.length && proximoTemConteudo ? q - 1 : q;
    for (let k = 0; k < vazios; k += 1) saida.push(novoParagrafo());
    atual = [];
  });
  if (atual.length) saida.push(novoParagrafo(semEspacoNoFim(atual)));
  return saida;
};

export const normalizarQuebras = doc => {
  const schema = doc.type.schema;
  const filhos = [];
  doc.forEach(no => {
    if (no.type === schema.nodes.paragraph && schema.nodes.hard_break) {
      filhos.push(...quebrasComoParagrafos(no, schema));
    } else {
      filhos.push(no);
    }
  });
  return schema.node('doc', doc.attrs, filhos);
};

const serializadorBase = new MarkdownSerializer(
  {
    ...MessageMarkdownSerializer.nodes,
    paragraph: paragrafoComEspaco,
    hard_break: quebraSemBarra,
  },
  {
    ...MessageMarkdownSerializer.marks,
    // Itálico gravado com sublinhado. Ver a explicação do asterisco acima:
    // é o que impede o itálico de virar negrito sozinho no próximo
    // salvamento. O negrito continua gravado com dois asteriscos, que é o que
    // o servidor entende.
    em: { ...MessageMarkdownSerializer.marks.em, open: '_', close: '_' },
  }
);

// O que o editor chama. Normaliza as quebras do Shift+Enter e então grava.
export const SerializadorComEspacos = {
  nodes: serializadorBase.nodes,
  marks: serializadorBase.marks,
  serialize: (doc, opcoes) =>
    serializadorBase.serialize(normalizarQuebras(doc), opcoes),
};

/**
 * ── O ASTERISCO ENQUANTO SE DIGITA ──────────────────────────────────────────
 * A regra do tokenizer acima só age quando o texto é LIDO. Mas o editor tem
 * uma segunda regra, que age na hora em que a pessoa fecha o asterisco:
 * `italicRegex2`, em `rules/marks.js` do pacote, que aplica ITÁLICO. Era ela
 * que o Victor via agindo — e, sem trocá-la, o texto era gravado com um
 * asterisco só e o servidor o lia como itálico.
 *
 * Esta regra é a mesma, com a marca trocada, e entra ANTES da do pacote:
 * em ProseMirror quem responde `handleTextInput` primeiro ganha, e os plugins
 * do consumidor são colocados no início da lista por `buildEditor`.
 */

// Expressão idêntica à `italicRegex2` do pacote.
const ASTERISCO_SIMPLES = /(\S*[^\s*]*)(\*([^\s*][^*]*[^\s*]|[^\s*])\*)$/;

// O pacote recusa quando já existe texto colado antes do asterisco, para não
// transformar `abc*texto*`; e abre exceção para as marcas que combinam com
// negrito. Sem isto, digitar `**texto*` (o caminho de quem escreve
// `**texto**`) dispararia no meio e estragaria o negrito de dois asteriscos.
const COMBINAM_COM_NEGRITO = ['__', '~~', '^'];

const prefixoAceitavel = (prefixo, state, inicio) => {
  if (!prefixo) return true;
  if (COMBINAM_COM_NEGRITO.includes(prefixo)) return true;
  const anterior = state.doc.resolve(inicio + prefixo.length).nodeBefore;
  return anterior?.type === state.schema.nodes.hard_break;
};

const aplicarNegrito = esquema => (state, match, inicio, fim) => {
  const negrito = esquema.marks.strong;
  const [, prefixo] = match;
  if (!prefixoAceitavel(prefixo, state, inicio)) return null;

  const de = inicio + prefixo.length;

  // Dentro de `código` o asterisco é literal, não formatação.
  if (esquema.marks.code?.isInSet(state.doc.resolve(de + 1).marks())) {
    return null;
  }

  // Não atravessa quebra de linha, igual ao pacote.
  let temQuebra = false;
  state.doc.nodesBetween(de, fim, no => {
    if (no.type === esquema.nodes.hard_break) {
      temQuebra = true;
      return false;
    }
    return !temQuebra;
  });
  if (temQuebra) return null;

  if (!state.doc.resolve(de).parent.type.allowsMarkType(negrito)) return null;

  // O asterisco de fechamento nem chega a ser inserido: devolver uma transação
  // consome a digitação. Só o de abertura precisa sair.
  return state.tr
    .addMark(de, fim, negrito.create())
    .delete(de, de + 1)
    .removeStoredMark(negrito);
};

export const criarRegrasDoAsterisco = esquema =>
  esquema.marks.strong
    ? [
        inputRules({
          rules: [new InputRule(ASTERISCO_SIMPLES, aplicarNegrito(esquema))],
        }),
      ]
    : [];

export const adaptadorComEspacos = {
  serializer: SerializadorComEspacos,
  createTransformer: criarTransformerComEspacos,
  plugins: criarRegrasDoAsterisco,
};
